'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

interface TabsContextValue {
  registerTrigger: (value: string, element: HTMLButtonElement | null) => void;
  activeValue: string | undefined;
  listRef: React.RefObject<HTMLDivElement | null>;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function Tabs({
  className,
  defaultValue,
  value,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  const [activeValue, setActiveValue] = React.useState(value || defaultValue);
  const triggersRef = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (value !== undefined) {
      setActiveValue(value);
    }
  }, [value]);

  const handleValueChange = (newValue: string) => {
    setActiveValue(newValue);
    onValueChange?.(newValue);
  };

  const registerTrigger = React.useCallback(
    (triggerValue: string, element: HTMLButtonElement | null) => {
      if (element) {
        triggersRef.current.set(triggerValue, element);
      } else {
        triggersRef.current.delete(triggerValue);
      }
    },
    [],
  );

  return (
    <TabsContext.Provider value={{ registerTrigger, activeValue, listRef }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn('flex flex-col gap-2', className)}
        defaultValue={defaultValue}
        value={value}
        onValueChange={handleValueChange}
        {...props}
      />
    </TabsContext.Provider>
  );
}

interface TabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  indicatorClassName?: string;
}

function TabsList({ className, children, indicatorClassName, ...props }: TabsListProps) {
  const context = React.useContext(TabsContext);
  const [indicatorStyle, setIndicatorStyle] = React.useState({ left: 0, width: 0 });
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const updateIndicator = () => {
      if (!listRef.current || !context?.activeValue) return;

      const activeTab = listRef.current.querySelector(
        `[data-state="active"]`,
      ) as HTMLButtonElement | null;

      if (activeTab) {
        const listRect = listRef.current.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();

        setIndicatorStyle({
          left: tabRect.left - listRect.left,
          width: tabRect.width,
        });
      }
    };

    updateIndicator();

    // Update on resize
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [context?.activeValue]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn(
        'text-muted-foreground relative inline-flex h-9 w-fit items-center justify-center p-[3px]',
        className,
      )}
      {...props}
    >
      {children}
      {/* Animated indicator */}
      <motion.div
        className={cn('absolute bottom-0 h-px', indicatorClassName ?? 'bg-foreground')}
        initial={false}
        animate={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 35,
        }}
      />
    </TabsPrimitive.List>
  );
}

function TabsTrigger({
  className,
  value,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const context = React.useContext(TabsContext);

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      value={value}
      ref={(el) => context?.registerTrigger(value as string, el)}
      className={cn(
        `text-muted-foreground hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50
        focus-visible:outline-ring data-[state=active]:text-foreground font-body-medium inline-flex flex-1
        cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap px-3 py-2 transition-colors
        duration-200 focus-visible:outline-1 focus-visible:ring-[3px] disabled:pointer-events-none
        disabled:opacity-50 data-[state=active]:cursor-default [&_svg:not([class*='size-'])]:size-4
        [&_svg]:pointer-events-none [&_svg]:shrink-0`,
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
